
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Test the returning user experience and Layer against realistic, dynamic Sandbox profiles'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Sandbox Profiles' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Both the Returning User Experience and the Layer product are based on profiles. Profiles are uniquely identified by phone number and correspond to an end user who has connected accounts to Plaid before and has opted in to saving their information. `}</p>
    <p>{`For testing profile-based products in Sandbox, Plaid provides a set of phone numbers that correspond to specific hard-coded user states; these are listed in `}<a parentName="p" {...{
        "href": "/docs/layer/#hard-coded-profiles"
      }}>{`Hard-coded profiles`}</a>{` for Layer and `}<a parentName="p" {...{
        "href": "/docs/link/returning-user/#legacy-seeded-phone-numbers"
      }}>{`Legacy seeded phone numbers`}</a>{` for the returning user experience. However, these profiles cannot be modified in any way, and institutions cannot be saved to them. For this reason, Plaid also offers flexible, modifiable profiles that act like real-world profiles. These are known as Sandbox Profiles. `}</p>
    <p>{`Sandbox profiles can be associated with any Sandbox test user. You may map them to Plaid's pre-populated, hard-coded Sandbox users or to custom Sandbox users. `}</p>
    <p>{`Profiles are managed in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/sandbox?tab=profiles"
      }}>{`Sandbox Studio profiles tab`}</a>{`.`}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-profiles-tab.png' alt="The Profiles tab of Sandbox Studio, showing the list of custom and starter profiles and a selected profile's phone number, connected Item, and options to launch Link or Layer." expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "using-a-profile"
    }}>{`Using a profile`}</h4>
    <p>{`You can either enter the phone number corresponding to the profile in the Sandbox Link flow, or from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/sandbox?tab=profiles"
      }}>{`Sandbox Studio profiles tab`}</a>{`, select the profile you want to use, and click `}<strong parentName="p">{`launch Link`}</strong>{`. `}</p>
    <p>{`All profiles use the two-factor code `}<inlineCode parentName="p">{`123456`}</inlineCode>{`. `}</p>
    <Image src='/assets/img/docs/sandbox-studio/sandbox-profiles-link-accounts.png' alt="The Link account selection screen for a returning user, listing the institution already connected to the profile alongside an option to add a new account." expandable={true} mdxType="Image" />
    <p>{`Adding an institution attaches it to the profile, so it is present the next time that phone number is used. Items created this way behave like any other Sandbox Item and can be used with the API explorer or with your own API calls.`}</p>
    <h4 {...{
      "id": "starter-profiles"
    }}>{`Starter profiles`}</h4>
    <p>{`Starter profiles are Plaid-defined profiles that begin with a set of connected accounts and realistic identity data. These profiles are extensible: you can connect more institutions to them and reset them back to their default data at any time. `}</p>
    <p>{`Starter profiles are pre-populated for the phone numbers `}<inlineCode parentName="p">{`415-555-0131`}</inlineCode>{` through `}<inlineCode parentName="p">{`415-555-0138`}</inlineCode>{`.`}</p>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Phone number`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Profile`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Identity`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Starting institutions`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Products`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0131`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`College Student`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Alberta Johnson`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tartan Bank`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity, Assets`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0132`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Small Business Owner`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Betsy Ross`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`First Gingham Credit Union, Tartan Bank`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity, Assets`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0133`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Retiree`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Paul Revere`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`First Platypus Bank, First Gingham Credit Union`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity, Investments, Assets`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0134`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Risk Review`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Jordan Lee`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tartan Bank`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity, Assets`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0135`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Affluent Investor`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Benjamin Franklin`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`First Platypus Bank, Tartan Bank`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity, Investments, Income, Assets`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0136`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Default Returning User`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Alberta Bobbeth Charleson`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tartan Bank, First Gingham Credit Union`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0137`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Trusted Multi-item`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`None`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tattersall FCU, Tartan Bank, First Gingham Credit Union`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`415-555-0138`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`OAuth Multi-item`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`None`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Tattersall FCU, Tartan Bank, First Gingham Credit Union, Platypus OAuth Bank`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Auth, Transactions, Identity`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <p>{`Note that `}<inlineCode parentName="p">{`415-555-0137`}</inlineCode>{` and `}<inlineCode parentName="p">{`415-555-0138`}</inlineCode>{` are intended for testing the saved-account picker: they are not associated with an identity, which means they are not eligible for `}<a parentName="p" {...{
        "href": "/docs/layer/#testing-layer-in-sandbox"
      }}>{`Layer`}</a>{`; use one of the other starter profiles for Layer testing.`}</p>
    <p>{`To restore a starter profile to its default data, use the reset button (the curved left-facing arrow icon) in the Dashboard.`}</p>
    <h4 {...{
      "id": "custom-profiles"
    }}>{`Custom profiles`}</h4>
    <p>{`Any valid US phone number that is not reserved by a starter profile creates a custom profile the first time it is used in Link. A custom profile starts empty and holds whatever institutions you connect to it.`}</p>
    <p>{`Use a custom profile when you want a returning user built entirely from your own `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`custom test data`}</a>{`.`}</p>
    <p>{`Custom profiles can be deleted via the Dashboard.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "using-a-profile",
        level: 4,
        title: "Using a profile",
      },
{
        id: "starter-profiles",
        level: 4,
        title: "Starter profiles",
      },
{
        id: "custom-profiles",
        level: 4,
        title: "Custom profiles",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
